import { useEffect, useMemo, useState } from "react";
import { useStore } from "@/lib/store";
import { ArtifactDetail } from "./ArtifactDetail";

function relativeTime(ts: number): string {
  const diff = Date.now() - ts;
  const m = Math.round(diff / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.round(h / 24);
  if (d < 7) return `${d}d ago`;
  return new Date(ts).toLocaleDateString();
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(2)} MB`;
}

export function LibraryView({ initialSelectedId, onExit }: { initialSelectedId?: string | null; onExit: () => void }) {
  const artifacts = useStore((s) => s.artifacts);
  const refreshArtifacts = useStore((s) => s.refreshArtifacts);
  const [selectedId, setSelectedId] = useState<string | null>(initialSelectedId ?? null);
  const [query, setQuery] = useState("");

  useEffect(() => {
    refreshArtifacts();
  }, [refreshArtifacts]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return artifacts;
    return artifacts.filter((a) =>
      a.name.toLowerCase().includes(q) || a.tags.some((t) => t.toLowerCase().includes(q)),
    );
  }, [artifacts, query]);

  useEffect(() => {
    if (selectedId && !artifacts.some((a) => a.id === selectedId)) {
      setSelectedId(null);
    }
  }, [artifacts, selectedId]);

  return (
    <div className="fixed inset-y-0 right-0 z-40 flex flex-col bg-background" style={{ left: "var(--sb-w, 0px)" }}>
      <div data-tauri-drag-region className="flex items-center gap-3 border-b px-4 py-3" style={{ paddingTop: "calc(0.75rem + var(--titlebar-h, 0px) + env(safe-area-inset-top))" }}>
        <button onClick={onExit} className="rounded-md p-1.5 text-muted-foreground hover:text-foreground" title="Back to chat">
          <i className="fa-solid fa-arrow-left" />
        </button>
        <h1 className="text-base font-semibold">Library</h1>
        <span className="text-xs text-muted-foreground">{artifacts.length} artifacts</span>
        <div className="ml-auto flex items-center gap-2">
          <input
            type="text"
            placeholder="Search…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="w-40 rounded-md border bg-muted/40 px-2.5 py-1 text-sm outline-none focus:border-primary sm:w-64"
          />
          <button
            onClick={() => refreshArtifacts()}
            className="rounded-md p-1.5 text-muted-foreground hover:text-foreground"
            title="Refresh"
          >
            <i className="fa-solid fa-rotate" />
          </button>
        </div>
      </div>

      <div className="flex min-h-0 flex-1">
        <div className={`overflow-y-auto border-r ${selectedId ? "hidden md:block md:w-72" : "w-full md:w-72"}`}>
          {filtered.length === 0 ? (
            <p className="px-4 py-6 text-center text-sm text-muted-foreground">
              {query ? "No matching artifacts." : "No artifacts yet. The LLM can create them via tools."}
            </p>
          ) : (
            <ul>
              {filtered.map((a) => (
                <li key={a.id}>
                  <button
                    onClick={() => setSelectedId(a.id)}
                    className={`flex w-full flex-col items-start gap-0.5 border-b px-4 py-2.5 text-left hover:bg-muted/50 ${a.id === selectedId ? "bg-muted" : ""}`}
                  >
                    <span className="truncate text-sm font-medium">{a.name}</span>
                    <span className="flex items-center gap-2 text-[11px] text-muted-foreground">
                      <span>{a.fileCount} files</span>
                      <span className="opacity-50">·</span>
                      <span>{formatBytes(a.totalBytes)}</span>
                      <span className="opacity-50">·</span>
                      <span>{relativeTime(a.updatedAt)}</span>
                    </span>
                    {a.tags.length > 0 && (
                      <span className="mt-1 flex flex-wrap gap-1">
                        {a.tags.map((t) => (
                          <span key={t} className="rounded-full bg-muted px-1.5 py-0.5 text-[10px] text-muted-foreground">
                            {t}
                          </span>
                        ))}
                      </span>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="flex-1 min-w-0">
          {selectedId ? (
            <ArtifactDetail id={selectedId} onClose={() => setSelectedId(null)} />
          ) : (
            <div className="flex h-full items-center justify-center p-6 text-sm text-muted-foreground">
              Select an artifact to inspect.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
